import axios from "axios";
import { SERVER_API } from '../config/config';
import { getAccessToken, setAccessToken, clearAuth } from '../stores/authStore';

// No instance-level withCredentials: data endpoints authenticate via the Bearer
// header only. Credentials are sent per-request solely where the httpOnly
// refreshToken cookie is involved (verify-otp, refresh, logout), so wildcard
// CORS on the backend can never break ordinary API calls.
const api = axios.create({
  baseURL: `${SERVER_API}`,
  headers: {
    "Content-Type": "application/json",
    "requestkey": process.env?.REACT_APP_REQUEST_KEY,
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = 'Bearer ' + token;
    }
    config.headers["requestkey"] = process.env?.REACT_APP_REQUEST_KEY;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// userType must be read before clearAuth() removes it from localStorage
const redirectToLogin = () => {
  const userType = localStorage.getItem('userType');
  clearAuth();
  window.location.href = userType === 'owner' ? '/partner' : '/signin';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Refresh token travels only in the httpOnly cookie, never the body (auth.md §1, §4)
        const { data } = await axios.post(
          `${SERVER_API}/users/refresh-token`,
          {},
          {
            withCredentials: true,
            headers: {
              "requestkey": process.env?.REACT_APP_REQUEST_KEY,
            },
          }
        );

        if (data.status && data.accessToken) {
          setAccessToken(data.accessToken);
          processQueue(null, data.accessToken);
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
          return api(originalRequest);
        }

        throw new Error(data.message || 'Refresh failed');
      } catch (refreshError) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
